import { useMemo } from "react";
import { useGetSettings } from "@/hooks";
import { IRawSettingsResData, ISelectableBaseProps } from "@/interfaces";
import { GATEWAY_SETTINGS_MAP, paymentGateways } from "@/constants";

interface IUseSettingsReturn {
  gateways: ISelectableBaseProps[];
  isLoading: boolean;
  error: Error | null;
  hasActiveGateways: boolean;
  settings: any;
}

export const useSettings = (token?: string): IUseSettingsReturn => {
  const { data: settingsData, isLoading, error } = useGetSettings(token);

  const filteredGateways = useMemo(() => {
    if (!settingsData?.data) {
      // If no settings data, return all gateways (fallback)
      return paymentGateways;
    }

    const settings = settingsData.data;

    // Filter gateways based on settings
    const activeGateways = paymentGateways.filter((gateway) => {
      // Check if gateway.key exists and is in the map
      if (!gateway.key || !(gateway.key in GATEWAY_SETTINGS_MAP)) {
        return false;
      }

      const settingKey = GATEWAY_SETTINGS_MAP[
        gateway.key
      ] as keyof IRawSettingsResData;
      return settings[settingKey] === true;
    });

    // Set first active gateway as selected
    if (activeGateways.length > 0) {
      return activeGateways.map((gateway, index) => ({
        ...gateway,
        isSelected: gateway.key === settings.payment_default,
      }));
    }

    // If no active gateways, return empty array or fallback
    return [];
  }, [settingsData]);

  return {
    settings: settingsData?.data,
    gateways: filteredGateways,
    isLoading,
    error: error as Error | null,
    hasActiveGateways: filteredGateways.length > 0,
  };
};
